import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../services/auth';

const LoginPage = () => {
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Login form state
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  // Register form state
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const result = await login(loginForm);
    
    if (!result.success) {
      const errorMessage = result.message || 'Login failed';
      // Add helpful CORS error message
      if (errorMessage.includes('Network Error') || errorMessage.includes('CORS')) {
        setMessage({ 
          type: 'danger', 
          text: 'Cannot connect to server. Please ensure Docker containers are running (docker-compose up -d)' 
        });
      } else {
        setMessage({ type: 'danger', text: errorMessage });
      }
    }
    
    setLoading(false);
  };

  // Handle register form submission
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (registerForm.password !== registerForm.confirmPassword) {
      setMessage({ type: 'danger', text: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    if (registerForm.password.length < 6) {
      setMessage({ type: 'danger', text: 'Password must be at least 6 characters long' });
      setLoading(false);
      return;
    }

    const result = await register({
      username: registerForm.username,
      email: registerForm.email,
      password: registerForm.password
    });
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Registration successful! Please login.' });
      setActiveTab('login');
      setRegisterForm({ username: '', email: '', password: '', confirmPassword: '' });
    } else {
      setMessage({ type: 'danger', text: result.message });
    }
    
    setLoading(false);
  };

  // Handle input changes
  const handleLoginChange = (e) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value
    });
  };

  const handleRegisterChange = (e) => {
    setRegisterForm({
      ...registerForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-vh-100 d-flex align-items-center">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="vienna-card fade-in">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold text-dark">
                    <i className="fas fa-building me-2" style={{ color: '#667eea' }}></i>
                    Vienna Budget Control
                  </h2>
                  <p className="text-muted">Track your Vienna trip expenses</p>
                </div>

                {message.text && (
                  <Alert variant={message.type} className="mb-4">
                    {message.text}
                  </Alert>
                )}

                <Tabs
                  activeKey={activeTab}
                  onSelect={(tab) => {
                    setActiveTab(tab);
                    setMessage({ type: '', text: '' });
                  }}
                  className="mb-4"
                >
                  <Tab eventKey="login" title="Login">
                    <Form onSubmit={handleLogin}>
                      <Form.Group className="mb-3">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          placeholder="Enter username"
                          value={loginForm.username}
                          onChange={handleLoginChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          placeholder="Enter password"
                          value={loginForm.password}
                          onChange={handleLoginChange}
                          required
                        />
                      </Form.Group>

                      <Button
                        type="submit"
                        className="vienna-btn-primary w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Signing in...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sign-in-alt me-2"></i>
                            Sign In
                          </>
                        )}
                      </Button>
                    </Form>
                  </Tab>

                  <Tab eventKey="register" title="Register">
                    <Form onSubmit={handleRegister}>
                      <Form.Group className="mb-3">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          placeholder="Choose username"
                          value={registerForm.username}
                          onChange={handleRegisterChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email (optional)</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="Enter email"
                          value={registerForm.email}
                          onChange={handleRegisterChange}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          placeholder="Choose password"
                          value={registerForm.password}
                          onChange={handleRegisterChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm password"
                          value={registerForm.confirmPassword}
                          onChange={handleRegisterChange}
                          required
                        />
                      </Form.Group>

                      <Button
                        type="submit"
                        className="vienna-btn-primary w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Creating account...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-user-plus me-2"></i>
                            Create Account
                          </>
                        )}
                      </Button>
                    </Form>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage; 